import type { JSX } from 'preact';
import { memo } from 'preact/compat';
import type { MapLook, SunSettings } from '../sim/map/index.ts';
import { ColorPicker, LiveSlider } from './controls.tsx';
import './lookSettings.css';

export type LightingLookGroupProps = {
  sun: SunSettings;
  look: MapLook;
  onSunLive: (sun: Partial<SunSettings>) => void;
  onSunCommit: () => void;
  onLookLive: (patch: Partial<MapLook>) => void;
  onLookCommit: () => void;
  onLookCancel?: () => void;
  showSun?: boolean;
  showWater?: boolean;
  showGridControls?: boolean;
  gridColorLabel?: string;
};

/** Shared sun + water + grid controls for the editor toolbar and the play Look section. */
export const LightingLookGroup = memo(function LightingLookGroup({
  sun,
  look,
  onSunLive,
  onSunCommit,
  onLookLive,
  onLookCommit,
  onLookCancel,
  showSun = true,
  showWater = true,
  showGridControls = true,
  gridColorLabel = 'Grid',
}: LightingLookGroupProps): JSX.Element | null {
  if (!showSun && !showWater && !showGridControls) {
    return null;
  }

  return (
    <div class="lighting-look-group">
      {showSun ? (
        <>
          <LiveSlider
            label="Sun position"
            value={sun.azimuth}
            minLabel="Dawn"
            maxLabel="Sunset"
            dataAttr="data-sun-azimuth"
            onInput={(azimuth) => onSunLive({ azimuth })}
            onCommit={onSunCommit}
          />
          <LiveSlider
            label="Sun height"
            value={sun.altitude}
            minLabel="Horizon"
            maxLabel="Zenith"
            dataAttr="data-sun-altitude"
            hint="Cast shade follows this slider, not Time."
            onInput={(altitude) => onSunLive({ altitude })}
            onCommit={onSunCommit}
          />
          <LiveSlider
            label="Sun strength"
            value={sun.strength}
            dataAttr="data-sun-strength"
            onInput={(strength) => onSunLive({ strength })}
            onCommit={onSunCommit}
          />
        </>
      ) : null}
      {showWater ? (
        <>
          <ColorPicker
            label="Water"
            value={look.waterColor}
            onChange={(waterColor) => onLookLive({ waterColor })}
            onCommit={onLookCommit}
            onCancel={onLookCancel}
          />
          <LiveSlider
            label="Water alpha"
            value={look.waterColor.a}
            dataAttr="data-water-alpha"
            onInput={(a) => onLookLive({ waterColor: { ...look.waterColor, a } })}
            onCommit={onLookCommit}
          />
        </>
      ) : null}
      {showGridControls ? (
        <>
          <ColorPicker
            label={gridColorLabel}
            value={look.gridColor}
            onChange={(gridColor) => onLookLive({ gridColor })}
            onCommit={onLookCommit}
            onCancel={onLookCancel}
          />
          <LiveSlider
            label="Grid alpha"
            value={look.gridColor.a}
            dataAttr="data-grid-alpha"
            onInput={(a) => onLookLive({ gridColor: { ...look.gridColor, a } })}
            onCommit={onLookCommit}
          />
        </>
      ) : null}
    </div>
  );
});
